import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { MessageCircle, Send, Loader2, CheckCircle, Clock, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DealQA({ opportunityId }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newQuestion, setNewQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchQA = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/investor/opportunities/${opportunityId}/qa`, { headers });
      setQuestions(res.data.questions || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [opportunityId]);

  useEffect(() => { fetchQA(); }, [fetchQA]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newQuestion.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/investor/opportunities/${opportunityId}/qa`, { question: newQuestion.trim() }, { headers });
      setNewQuestion("");
      fetchQA();
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white" data-testid="deal-qa-section">
      <button onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#faf7f2] transition-colors rounded-t-xl"
        data-testid="qa-toggle">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-emerald-600" />
          <span className="text-xs font-bold text-[#333] uppercase tracking-wider">Q&A ({questions.length})</span>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-[#999]" /> : <ChevronDown className="h-4 w-4 text-[#999]" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
            <div className="px-4 pb-4 space-y-3">
              {/* Ask question form */}
              <form onSubmit={handleSubmit} className="flex gap-2" data-testid="qa-form">
                <input type="text" value={newQuestion} onChange={e => setNewQuestion(e.target.value)}
                  placeholder="Ask a question about this deal..."
                  className="flex-1 px-3 py-2 rounded-lg border border-[#e8e0d0] text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none bg-[#faf7f2]"
                  data-testid="qa-input" />
                <button type="submit" disabled={submitting || !newQuestion.trim()}
                  className="px-3 py-2 rounded-lg bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-40 transition-colors"
                  data-testid="qa-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </form>

              {/* Questions list */}
              {loading ? (
                <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>
              ) : questions.length === 0 ? (
                <p className="text-xs text-[#999] text-center py-3">No questions yet. Be the first to ask!</p>
              ) : (
                <div className="space-y-2.5 max-h-64 overflow-y-auto">
                  {questions.map((q, i) => (
                    <motion.div key={q.qa_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="rounded-lg bg-[#faf7f2] border border-[#e8e0d0] p-3" data-testid={`qa-item-${q.qa_id}`}>
                      <div className="flex items-start gap-2">
                        <MessageCircle className="h-3.5 w-3.5 text-blue-500 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#333] font-medium">{q.question}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {q.is_own && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-600 font-semibold">You</span>}
                            <span className="text-[9px] text-[#bbb]">{new Date(q.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                      {q.answer ? (
                        <div className="mt-2 ml-5 pl-3 border-l-2 border-emerald-300">
                          <p className="text-sm text-[#555]">{q.answer}</p>
                          <div className="flex items-center gap-1.5 mt-1">
                            <CheckCircle className="h-3 w-3 text-emerald-500" />
                            <span className="text-[9px] text-emerald-600 font-medium">{q.answered_by_name || "Team"}</span>
                            <span className="text-[9px] text-[#ccc]">{q.answered_at ? new Date(q.answered_at).toLocaleDateString() : ""}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2 ml-5 flex items-center gap-1.5 text-[10px] text-[#bbb]">
                          <Clock className="h-3 w-3" /> Awaiting response
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
